import { type Node, type XYPosition } from '@xyflow/react';

import { type DiagramSide } from '@/types/schema';

import { type DiagramBuilder_DiagramFragment } from '../graphql/Diagram.generated';
import {
  type DiagramBuilderEdge,
  type DiagramBuilderNode,
  type DiagramBuilderNodeStyle,
  type DiagramBuilderStage,
} from './diagram';

/*
 * Each edit returns a new diagram and does not change its input. The editor
 * gives the new diagram to its undo history as one step.
 */

function withContent(
  diagram: DiagramBuilder_DiagramFragment,
  changes: Partial<DiagramBuilder_DiagramFragment['content']>
): DiagramBuilder_DiagramFragment {
  return { ...diagram, content: { ...diagram.content, ...changes } };
}

/** The kind, text, and style of a node that the editor adds. */
export type NewDiagramNode = Pick<
  DiagramBuilderNode,
  'kind' | 'label' | 'nodeStyle'
>;

/**
 * Adds `node` with empty notes and no estate category at `position`. The new
 * node goes in `stageId`, or in the first stage when `stageId` is not set.
 */
export function addDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  nodeID: string,
  position: XYPosition,
  node: NewDiagramNode,
  stageId?: string | null
): DiagramBuilder_DiagramFragment {
  const { content } = diagram;
  return withContent(diagram, {
    nodes: [
      ...content.nodes,
      {
        ...node,
        nodeID,
        detail: '',
        stageId: stageId ?? content.stages[0]?.id,
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      },
    ],
  });
}

/** The two ends of a connection: a node and the side of the node. */
export interface DiagramConnectionEnds {
  sourceNodeID: string;
  targetNodeID: string;
  sourceSide: DiagramSide;
  targetSide: DiagramSide;
}

/**
 * Adds a connection without a label between the sides of two nodes. A
 * connection without a label shows no label.
 */
export function addDiagramConnection(
  diagram: DiagramBuilder_DiagramFragment,
  edgeID: string,
  ends: DiagramConnectionEnds
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    edges: [...diagram.content.edges, { edgeID, label: '', ...ends }],
  });
}

/**
 * Moves the ends of a connection to other nodes or sides. The manual bends
 * are valid only for the old ends, so the connection loses them.
 */
export function reconnectDiagramEdge(
  diagram: DiagramBuilder_DiagramFragment,
  edgeID: string,
  ends: DiagramConnectionEnds
): DiagramBuilder_DiagramFragment {
  return editDiagramEdge(diagram, edgeID, { ...ends, waypoints: null });
}

/** The tile fields that the tile shows as text. They set the tile height. */
const TILE_TEXT_FIELDS = ['label', 'subtitle', 'detail'] as const;

/**
 * Applies `changes` to the tile with this ID. When `changes` gives new text,
 * the tile also loses its saved height, so that it fits its new content.
 * Style changes keep the saved height.
 */
export function editDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  id: string,
  changes: Partial<DiagramBuilderNode>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) => {
      if (node.nodeID !== id) return node;
      // A missing subtitle can be undefined or null. Both are the same text.
      const textChanged = TILE_TEXT_FIELDS.some(
        (field) =>
          field in changes && (changes[field] ?? null) !== (node[field] ?? null)
      );
      const next = { ...node, ...changes };
      if (!textChanged) return next;
      return { ...next, nodeStyle: { ...next.nodeStyle, height: null } };
    }),
  });
}

/** Applies `changes` to the connection with this ID. */
export function editDiagramEdge(
  diagram: DiagramBuilder_DiagramFragment,
  id: string,
  changes: Partial<DiagramBuilderEdge>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    edges: diagram.content.edges.map((edge) =>
      edge.edgeID === id ? { ...edge, ...changes } : edge
    ),
  });
}

/** Applies the style `changes` to each node in `ids`. */
export function editDiagramNodeStyles(
  diagram: DiagramBuilder_DiagramFragment,
  ids: ReadonlySet<string>,
  changes: Partial<DiagramBuilderNodeStyle>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) =>
      ids.has(node.nodeID)
        ? { ...node, nodeStyle: { ...node.nodeStyle, ...changes } }
        : node
    ),
  });
}

/**
 * The style changes that show or hide the notes or the footer of a tile. A
 * shown field saves null, the contract default.
 */
export function getTextVisibilityStyle(
  field: 'notes' | 'footer',
  show: boolean
): Partial<DiagramBuilderNodeStyle> {
  const hide = show ? null : true;
  return field === 'notes' ? { hideDetail: hide } : { hideFooterLabel: hide };
}

/** Applies `changes` to the stage with this ID. */
export function editDiagramStage(
  diagram: DiagramBuilder_DiagramFragment,
  id: string,
  changes: Partial<DiagramBuilderStage>
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    stages: diagram.content.stages.map((stage) =>
      stage.id === id ? { ...stage, ...changes } : stage
    ),
  });
}

/** The label of a new line between two stages. The user edits it on the band. */
export const NEW_STAGE_BOUNDARY_LABEL = 'New event';

/**
 * Adds a line across the canvas at height `y`: a boundary between two
 * stages. The stage with tiles above and below `y` splits in two. Its tiles
 * whose top is at or below `y` move to a new stage, with ID `stageID`, just
 * after it. A diagram without stages first gets one stage for all its tiles.
 *
 * When no stage has tiles on both sides of `y`, the diagram does not change:
 * a line there would be on an existing boundary or outside the tiles. Call
 * it after the tile positions are saved.
 */
export function addDiagramStageBoundary(
  diagram: DiagramBuilder_DiagramFragment,
  stageIDs: { first: string; next: string },
  y: number
): DiagramBuilder_DiagramFragment {
  const { content } = diagram;
  const stages: DiagramBuilderStage[] = content.stages.length
    ? content.stages
    : [{ __typename: 'DiagramStage', id: stageIDs.first, deathEvent: null }];
  const stageOf = (node: DiagramBuilderNode) =>
    content.stages.length ? node.stageId : stageIDs.first;
  const isBelow = (node: DiagramBuilderNode) => (node.position?.y ?? 0) >= y;
  const split = stages.find((stage) => {
    const nodes = content.nodes.filter((node) => stageOf(node) === stage.id);
    return nodes.some(isBelow) && nodes.some((node) => !isBelow(node));
  });
  if (!split) return diagram;
  const index = stages.indexOf(split);
  return withContent(diagram, {
    stages: [
      ...stages.slice(0, index + 1),
      {
        __typename: 'DiagramStage',
        id: stageIDs.next,
        deathEvent: NEW_STAGE_BOUNDARY_LABEL,
      },
      ...stages.slice(index + 1),
    ],
    nodes: content.nodes.map((node) => {
      const stageId = stageOf(node);
      return {
        ...node,
        stageId:
          stageId === split.id && isBelow(node) ? stageIDs.next : stageId,
      };
    }),
  });
}

/**
 * Removes the saved position of each tile and the manual bends of each
 * connection, so that the automatic layout places and routes all of them.
 */
export function resetDiagramPositions(
  diagram: DiagramBuilder_DiagramFragment
): DiagramBuilder_DiagramFragment {
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) => ({ ...node, position: null })),
    edges: diagram.content.edges.map((edge) => ({ ...edge, waypoints: null })),
  });
}

/**
 * Saves the position of each tile in `tiles` after the user drags or resizes
 * `tile`. `tile` gives its own position, and its width and height when it has
 * them. React Flow sets them after a resize. The saved width and height are
 * whole numbers of pixels. The other tiles keep their current positions, so
 * the automatic layout does not move them later.
 *
 * The manual bends of a connection are valid only for the positions of its
 * tiles, so the connections of `tile` lose their bends.
 */
export function placeDiagramNode(
  diagram: DiagramBuilder_DiagramFragment,
  tile: Pick<Node, 'id' | 'position' | 'width' | 'height'>,
  tiles: Pick<Node, 'id' | 'position'>[]
): DiagramBuilder_DiagramFragment {
  const positions = new Map(tiles.map((node) => [node.id, node.position]));
  positions.set(tile.id, tile.position);
  return withContent(diagram, {
    edges: diagram.content.edges.map((edge) =>
      edge.sourceNodeID === tile.id || edge.targetNodeID === tile.id
        ? { ...edge, waypoints: null }
        : edge
    ),
    nodes: diagram.content.nodes.map((node) => {
      const position = positions.get(node.nodeID) ?? node.position;
      if (node.nodeID !== tile.id) return { ...node, position };
      return {
        ...node,
        position,
        nodeStyle: {
          ...node.nodeStyle,
          ...(tile.width && { width: Math.round(tile.width) }),
          ...(tile.height && { height: Math.round(tile.height) }),
        },
      };
    }),
  });
}

/**
 * Saves the current canvas position of each tile without a saved position.
 * Call it before an edit that changes the automatic layout input, such as a
 * new connection, so that the automatic layout does not move tiles that the
 * user sees in place.
 */
export function pinDiagramPositions(
  diagram: DiagramBuilder_DiagramFragment,
  tiles: Pick<Node, 'id' | 'position'>[]
): DiagramBuilder_DiagramFragment {
  const positions = new Map(tiles.map((node) => [node.id, node.position]));
  return withContent(diagram, {
    nodes: diagram.content.nodes.map((node) => {
      const position = positions.get(node.nodeID);
      if (node.position || !position) return node;
      return {
        ...node,
        position: { x: Math.round(position.x), y: Math.round(position.y) },
      };
    }),
  });
}

/** Remove a tile or connection and its dependent edges as one undoable edit. */
export function removeDiagramElement(
  diagram: DiagramBuilder_DiagramFragment,
  id: string
): DiagramBuilder_DiagramFragment {
  const { content } = diagram;
  const removed = new Set([
    id,
    ...content.edges
      .filter((edge) => edge.sourceNodeID === id || edge.targetNodeID === id)
      .map((edge) => edge.edgeID),
  ]);
  return withContent(diagram, {
    nodes: content.nodes.filter((node) => !removed.has(node.nodeID)),
    edges: content.edges.filter((edge) => !removed.has(edge.edgeID)),
  });
}
